<script lang="ts">
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import {
		getStructuredDataScript,
		type StructuredDataSchema,
	} from "../../../../core/schema/utils/getStructuredDataScript.js";
	import { getContext } from "svelte";
	import { pageSlugContextKey } from "../../contexts/pageSlugContextKey.js";
	import type { Category } from "../../../../core/schema/Category.js";
	import { appName } from "../../../../core/schema/names/appName.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import type { Price } from "../../../../core/schema/Price.js";
	import type { ProductPricesByLevel } from "../../../../core/schema/ProductPricesByLevel.js";
	import { productSizeKeyReversedMap } from "../../../../core/schema/productSizeKeyReversedMap.js";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import { productSizeNameMap } from "../../../../core/schema/names/productSizeNameMap.js";
	import { getDefaultSizeForProductForCustomer } from "../../../../core/schema/utils/getDefaultSizeForProductForCustomer.js";
	import { getImagesForProduct } from "../../../../core/schema/utils/getImagesForProduct.js";

	export let product: ProductForCustomer;
	export let productCategory: Category;
	export let description: string;

	const { STORAGE_URL, TENANT_LOCALE, WEB_URL } = getGlobalSettings();
	const url = `${WEB_URL}/${getContext<string>(pageSlugContextKey)}`;
	const productName = product.name[TENANT_LOCALE];
	const category = productCategory.name[TENANT_LOCALE];
	const keywords = product.metadata.keywords?.[TENANT_LOCALE];
	const availability =
		product.availableSizes.length > 0
			? ("https://schema.org/InStock" as const)
			: ("https://schema.org/OutOfStock" as const);

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	function getStructuredDataForProduct(size: keyof ProductPricesByLevel) {
		const price = product.priceForCustomer[size];
		if (!price) {
			throw new Error(`Price for size ${size} not found`);
		}
		const sizeValue = productSizeKeyReversedMap[size];
		return {
			"@context": "https://schema.org" as const,
			"@type": "Product" as const,
			name: productName,
			description,
			image: getImagesForProduct(product, sizeValue).map((image) => `${STORAGE_URL}/${image.path}`),
			url: `${url}?size=${sizeValue}`,
			category,
			keywords,
			brand: { "@type": "Brand" as const, name: appName },
			size: productSizeNameMap[sizeValue][TENANT_LOCALE],
			offers: {
				"@type": "Offer" as const,
				availability,
				price: price.withTax,
				priceCurrency: "CZK" as const,
				shippingDetails: {
					"@type": "OfferShippingDetails" as const,
					doesNotShip: true as const,
				},
				hasMerchantReturnPolicy: {
					"@type": "MerchantReturnPolicy" as const,
					returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted" as const,
					applicableCountry: "CZ",
				},
			},
		} satisfies StructuredDataSchema;
	}

	const script = getStructuredDataScript(
		doesProductForCustomerHaveMultipleSizes(product)
			? {
					"@context": "https://schema.org",
					"@type": "ProductGroup",
					name: productName,
					description,
					image: getImagesForProduct(product, getDefaultSizeForProductForCustomer(product)).map(
						(image) => `${STORAGE_URL}/${image.path}`,
					),
					url,
					category,
					keywords,
					brand: { "@type": "Brand", name: appName },
					productGroupID: product.id,
					variesBy: ["https://schema.org/size"],
					hasVariant: (Object.entries(product.priceForCustomer) as [keyof ProductPricesByLevel, Price | undefined][])
						.filter(([_, value]) => value !== undefined)
						.map(([size]) => getStructuredDataForProduct(size)),
				}
			: getStructuredDataForProduct(productSizeKeyMap[getDefaultSizeForProductForCustomer(product)]),
	);

	/* eslint-disable svelte/no-at-html-tags */
</script>

<svelte:head>
	{@html script}
</svelte:head>
